import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { ChevronLeft, RotateCcw } from 'lucide-react';
import { Link } from 'react-router';
import type { ActivityEntry, ActivityKind } from '@kratispot/shared';
import { HoverNote } from '@/components/hover-note';
import { Toasts, useToasts } from '@/components/toasts';
import { Button } from '@/components/ui/button';
import { actOnReservation, fetchActivity } from '@/lib/api';
import { timeOf } from '@/lib/format';
import { useTranslation, type CopyKey } from '@/lib/i18n';

const KIND_COPY: Readonly<Record<ActivityKind, CopyKey>> = {
  reserved: 'evReserved',
  checkedIn: 'evCheckedIn',
  cancelled: 'evCancelled',
  expired: 'evExpired',
};

// What stands in a Restore's way, in the language on screen: §10 meets §13.
export function blockedBy(entry: ActivityEntry, t: (key: CopyKey) => string): string | null {
  if (entry.refunded) return t('restoreRefunded');
  return entry.conflict === null ? null : `${t('restoreBlocked')} ${entry.conflict}`;
}

type Feed =
  | { phase: 'loading' | 'failed' }
  | { phase: 'ready'; entries: readonly ActivityEntry[]; timezone: string };

// Who did what to which booking, and the way back from it. §10
export function ActivityPage({ venueId }: { venueId: string }): ReactElement {
  const { t, language } = useTranslation();
  const [feed, setFeed] = useState<Feed>({ phase: 'loading' });
  const [busy, setBusy] = useState(false);
  const { toasts, push } = useToasts();

  const load = useCallback(
    (signal?: AbortSignal) =>
      fetchActivity(venueId, signal)
        .then((answer) =>
          setFeed({ phase: 'ready', entries: answer.entries, timezone: answer.timezone }),
        )
        .catch(() => {
          if (signal?.aborted !== true) setFeed({ phase: 'failed' });
        }),
    [venueId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function restore(entry: ActivityEntry): Promise<void> {
    setBusy(true);
    try {
      await actOnReservation(venueId, entry.reservationId, 'restore');
      push(t('restored'), 'bg-emerald-600');
    } catch {
      push(t('restoreFailed'), 'bg-red-500');
    }
    setBusy(false);
    await load();
  }

  if (feed.phase !== 'ready')
    return (
      <p
        role={feed.phase === 'failed' ? 'alert' : undefined}
        className="p-6 text-center text-sm text-muted-foreground"
      >
        {t(feed.phase === 'loading' ? 'loading' : 'errorGeneric')}
      </p>
    );

  return (
    <section className="mx-auto flex w-full max-w-[640px] flex-col gap-3 p-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="outline" size="sm">
          <Link to="/">
            <ChevronLeft className="size-4" aria-hidden />
            {t('back')}
          </Link>
        </Button>
        <h2 className="truncate text-[15px] font-bold">{t('activity')}</h2>
      </div>
      {feed.entries.length === 0 && (
        <p className="rounded-xl border p-6 text-center text-sm text-muted-foreground">
          {t('activityEmpty')}
        </p>
      )}
      <ul className="flex flex-col rounded-2xl border">
        {feed.entries.map((entry) => (
          <li
            key={entry.id}
            data-testid={`entry-${entry.id}`}
            className="flex items-center gap-3 border-b p-3 last:border-b-0"
          >
            <span className="min-w-0 flex-1 text-[13px]">
              <b>{t(KIND_COPY[entry.kind])}</b> #{entry.spotNumber} · {entry.guestName}
              <span className="block text-xs text-muted-foreground">
                {timeOf(entry.at, feed.timezone, language)} · {entry.actorName ?? t('bySystem')} ·{' '}
                {entry.code}
              </span>
            </span>
            {entry.restorable &&
              (blockedBy(entry, t) === null ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void restore(entry)}
                >
                  <RotateCcw className="size-4" aria-hidden />
                  {t('restore')}
                </Button>
              ) : (
                // aria-disabled, not disabled: a disabled control never sees a hover.
                <HoverNote label={blockedBy(entry, t) as string}>
                  <Button size="sm" variant="outline" aria-disabled className="opacity-50">
                    <RotateCcw className="size-4" aria-hidden />
                    {t('restore')}
                  </Button>
                </HoverNote>
              ))}
          </li>
        ))}
      </ul>
      <Toasts toasts={toasts} undoLabel={t('undo')} />
    </section>
  );
}
